import React, { useState, useEffect } from 'react';
import { useOrion } from '../context/OrionContext';
import { TIPOS, JUSTIFICATIVA_MAX, formatarDuracao } from '../services/atividadeApi';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// Pílula de atividade — o status do operador, sempre à vista.
//
// Mostra se ele está Ativo ou parado (e por quê), com o tempo correndo desde
// que o período começou. Clicar abre o modal de troca de status.
//
// Só aparece para quem tem jornada cadastrada em orion.operador: a diretoria
// não entra no controle e não vê a pílula.
// ─────────────────────────────────────────────────────────────────────────────

/** Conta o tempo na tela, para "há quanto tempo" não ficar congelado. */
function TempoCorrendo({ desde }) {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 30000);   // minuto a minuto basta
    return () => clearInterval(id);
  }, []);
  return <>{formatarDuracao((agora - new Date(desde).getTime()) / 60000)}</>;
}

/** Modal de troca de status. */
function ModalStatus({ atividade, aoFechar }) {
  const { periodo, colegas, definir, podeVoltarAtivo } = atividade;

  const [tipo, setTipo] = useState(periodo?.tipo || 'almoco');
  const [apoio, setApoio] = useState(periodo?.apoioCodigo ? String(periodo.apoioCodigo) : '');
  const [justificativa, setJustificativa] = useState(periodo?.observacao || '');
  const [gravando, setGravando] = useState(false);

  const colegaEscolhido = colegas.find(c => String(c.codigo) === apoio);
  // "Apoio" exige dizer a quem; "outro" exige a justificativa — senão o período
  // fica registrado sem informação nenhuma sobre o motivo.
  const podeGravar = tipo !== 'apoio' ? (tipo !== 'outro' || justificativa.trim().length > 0) : !!colegaEscolhido;

  const marcar = async (status) => {
    if (gravando) return;
    setGravando(true);
    try {
      await definir(status, status === 'inativo' ? tipo : null, {
        apoioCodigo: colegaEscolhido?.codigo || null,
        apoioNome: colegaEscolhido?.nome || null,
        observacao: tipo === 'outro' ? justificativa.trim() : null,
      });
      aoFechar();
    } finally {
      setGravando(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={aoFechar}>
      <div className="modal-nx" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="drawer-title">Meu status</div>
          <button className="drawer-close" onClick={aoFechar}><Icon name="x" size={15} /></button>
        </div>

        <div className="drawer-body">
          <label className="field-label">Motivo da pausa</label>
          <select value={tipo} onChange={e => setTipo(e.target.value)}>
            {Object.entries(TIPOS)
              // A ociosidade não se escolhe: quem marca é o monitor.
              .filter(([chave]) => chave !== 'ociosidade')
              .map(([chave, info]) => (
                <option key={chave} value={chave}>{info.rotulo}</option>
              ))}
          </select>

          {tipo === 'apoio' && (
            <div style={{ marginTop: 16 }}>
              <label className="field-label">Apoiando quem</label>
              <select value={apoio} onChange={e => setApoio(e.target.value)}>
                <option value="">— selecione o colega —</option>
                {colegas.map(c => <option key={c.codigo} value={c.codigo}>{c.nome}</option>)}
              </select>
            </div>
          )}

          {tipo === 'outro' && (
            <div style={{ marginTop: 16 }}>
              <label className="field-label">Justificativa</label>
              <input
                type="text"
                maxLength={JUSTIFICATIVA_MAX}
                value={justificativa}
                onChange={e => setJustificativa(e.target.value)}
                placeholder="Ex.: reunião"
              />
              <div className="campo-dica" style={{ marginTop: 6 }}>
                Até {JUSTIFICATIVA_MAX} caracteres — é um rótulo do motivo, não um relato.
              </div>
            </div>
          )}

          {/* Mesma leitura de cor da pílula na topbar: verde trabalhando,
              âmbar parado. Quem abre o painel confere o estado pela cor. */}
          {periodo && (
            <div
              className={`panel panel-dest ${periodo.status === 'ativo' ? 'd-ok' : 'd-atual'}`}
              style={{ marginTop: 18, marginBottom: 0 }}
            >
              Agora: <b>{periodo.status === 'ativo' ? 'Ativo' : (TIPOS[periodo.tipo]?.rotulo || 'Inativo')}</b>
              {' '}há <TempoCorrendo desde={periodo.inicio} />
              {periodo.apoioNome ? ` · apoiando ${periodo.apoioNome}` : ''}
              {!podeVoltarAtivo && (
                <div className="campo-dica" style={{ marginTop: 6 }}>
                  Apurado pelo MySuite a partir da sua última interação em um ticket.
                  Assim que você interagir de novo, o status volta a Ativo sozinho.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="drawer-foot">
          {/* "Estou ativo" sai de uma pausa DECLARADA (almoço, café, apoio…).
              Ele não desfaz uma ociosidade APURADA pelo MySuite: essa termina
              quando aparece a próxima interação num ticket, e o monitor
              devolve o status para Ativo sozinho. Sem isso, bastaria um clique
              para apagar o tempo parado do relatório. */}
          <button
            className="btn btn-ghost"
            onClick={() => marcar('ativo')}
            disabled={gravando || !podeVoltarAtivo}
            title={podeVoltarAtivo
              ? 'Voltar para Ativo'
              : 'A ociosidade é apurada pelo MySuite: o status volta a Ativo assim que você interagir com um ticket.'}
          >
            <Icon name="check" size={14} /> Estou ativo
          </button>
          <button className="btn btn-primary" onClick={() => marcar('inativo')} disabled={gravando || !podeGravar}>
            <Icon name="clock" size={14} /> Marcar pausa
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PilulaAtividade() {
  const { atividade } = useOrion();
  const [aberto, setAberto] = useState(false);

  if (!atividade?.ativo) return null;

  const { periodo } = atividade;
  const inativo = periodo?.status === 'inativo';
  const info = inativo ? (TIPOS[periodo.tipo] || { rotulo: 'Inativo', icone: 'clock' }) : null;

  return (
    <>
      <button
        className={`ativ-pill ${inativo ? 'ativ-pill-inativo' : 'ativ-pill-ativo'}`}
        onClick={() => setAberto(true)}
        title="Trocar meu status"
      >
        <span className="ativ-dot" />
        {inativo ? info.rotulo : 'Ativo'}
        {periodo && <span className="ativ-tempo"><TempoCorrendo desde={periodo.inicio} /></span>}
      </button>

      {aberto && <ModalStatus atividade={atividade} aoFechar={() => setAberto(false)} />}
    </>
  );
}
